
/**
 * Asset Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { CreateAssetSchema, UpdateAssetSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';

export class AssetService {
    constructor(api) {
        this.api = api;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await this.api.getAll(params);
            return {
                success: true,
                data: response.data,
                pendingCreateRows: response.pendingCreateRows || [],
                meta: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues || {},
                assetVisibilityMeta: response.assetVisibilityMeta,
                message: response.message || 'Asset listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset list');
        }
    }

    async createFilteredReportViewerSession(payload) {
        try {
            return await this.api.createFilteredReportViewerSession(payload);
        } catch (error) {
            throw this._handleError(error, 'Failed to create filtered asset report viewer session');
        }
    }


    async exportFilteredReport(payload) {
        try {
            return await this.api.exportFilteredReport(payload);
        } catch (error) {
            throw this._handleError(error, 'Failed to export filtered asset report');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await this.api.getById(id);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset');
        }
    }

    async getInventoryLinks(id) {
        try {
            const response = await this.api.getInventoryLinks(id);
            return {
                success: response.success !== false,
                data: response.data
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch inventory links');
        }
    }

    async getParty(id) {
        try {
            const response = await this.api.getParty(id);
            const body = response || {};
            return {
                success: body.success !== false,
                data: body.data,
                partyTemplate: body.partyTemplate,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset party');
        }
    }

    async getPartyTemplate(params = {}) {
        try {
            const response = await this.api.getPartyTemplate(params);
            const body = response || {};
            return {
                success: body.success !== false,
                data: body.data,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset party template');
        }
    }

    async getWorkflowTemplateOptions(params = {}) {
        try {
            const response = await this.api.getWorkflowTemplateOptions(params);
            const body = response || {};
            return {
                success: body.success !== false,
                data: body.data || [],
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset workflow template options');
        }
    }

    async syncParty(id, payload) {
        try {
            const response = await this.api.syncParty(id, payload);
            const body = response || {};
            return {
                success: body.success !== false,
                data: body.data,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to sync asset party');
        }
    }

    /**
     * Same payload as getInventoryLinks (GRC links + licenses, disposals, lifecycle event summary).
     * @param {string} id
     */
    async getAssetDependencySummary(id) {
        return this.getInventoryLinks(id);
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const validation = CreateAssetSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid data', validation.error);
        }

        try {
            const response = await this.api.create(validation.data);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Asset oluşturuldu',
                pendingChangeId: response.pendingChangeId,
                approvalStatus: response.approvalStatus,
                merged: response.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create asset');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const raw = data && typeof data === 'object' ? { ...data } : {};
        const partyPayload = raw.partyPayload;
        delete raw.partyPayload;

        const validation = UpdateAssetSchema.safeParse(raw);
        if (!validation.success) {
            throw ApiError.validation('Invalid data', validation.error);
        }

        const body = { ...validation.data };
        if (partyPayload != null) {
            body.partyPayload = partyPayload;
        }

        try {
            const response = await this.api.update(id, body);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Asset güncellendi',
                pendingChangeId: response.pendingChangeId || response.data?.pendingChangeId,
                approvalStatus: response.approvalStatus || response.data?.approvalStatus,
                merged: response.merged === true || response.data?.merged === true,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update asset');
        }
    }

    async submitPendingUpdate(id) {
        try {
            const response = await this.api.submitPendingUpdate(id);
            return {
                success: response.success !== false,
                data: response.data,
                message: response.message,
                pendingChangeId: response.data?.pendingChangeId,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit pending update');
        }
    }

    async getPendingUpdate(id) {
        try {
            const response = await this.api.getPendingUpdate(id);
            return { success: response.success !== false, data: response.data || [] };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch pending update');
        }
    }

    async getPendingUpdateSummary(ids) {
        try {
            const response = await this.api.getPendingUpdateSummary(ids);
            return { success: response.success !== false, data: response.data || {} };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch pending update summary');
        }
    }

    async getPendingUpdateById(draftId) {
        try {
            const response = await this.api.getPendingUpdateById(draftId);
            return { success: response.success !== false, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch pending update draft');
        }
    }

    async getWorkflowApprovalDetail(id) {
        try {
            const response = await this.api.getWorkflowApprovalDetail(id);
            return { success: response.success !== false, data: response.data };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch workflow approval detail');
        }
    }

    async stageAssetLink(id, delta) {
        try {
            const response = await this.api.stageAssetLink(id, delta);
            return {
                success: response.success !== false,
                gated: response.gated,
                draftSaved: response.draftSaved === true,
                approvalStatus: response.approvalStatus,
                pendingChangeId: response.pendingChangeId,
                message: response.message,
                merged: response.merged === true,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to stage asset link change');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: response.success !== false,
                data: response.data,
                message: response.message || 'Asset silindi',
                pendingChangeId: response.pendingChangeId,
                approvalStatus: response.approvalStatus,
                merged: response.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete asset');
        }
    }

    /**
     * Batch delete items via /batch-delete endpoint
     * @param {Array<string>} ids
     * @returns {Promise<Object>}
     */
    async batchDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch delete assets');
        }
    }

    /**
     * Custom operation: getStatistics
     */
    async getStatistics(params = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/statistics`, {
                params
            });
            const body = response.data || {};
            const statsPayload = body.data !== undefined ? body.data : body;
            return {
                success: body.success !== false,
                data: statsPayload,
                message: body.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute getStatistics');
        }
    }

    /**
     * Full-tenant relationship map (nodes + edges) for list-page graph tab.
     */
    async getRelationshipMap(params = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/relationship-map`, { params });
            const body = response.data || {};
            const payload = body.data !== undefined ? body.data : body;
            return {
                success: body.success !== false,
                data: payload,
                message: body.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute getRelationshipMap');
        }
    }

    /**
     * Custom operation: getRelationships
     */
    async getRelationships(id, data = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/${id}/relationships`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute getRelationships');
        }
    }

    /**
     * Custom operation: createRelationships
     */
    async createRelationships(id, data = {}) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/relationships`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createRelationships');
        }
    }

    /**
     * Custom operation: deleteRelationships
     */
    async deleteRelationships(id, data = {}) {
        try {
            const response = await this.api.client.delete(`${this.api.basePath}/${id}/relationships`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute deleteRelationships');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }

    /**
     * Get paginated related risks for an Asset
     * @param {string} id - Asset ID
     * @param {Object} params - Query parameters (page, limit, search, sort, filter)
     */
    async getRelatedRisks(id, params = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/${id}/risks`, { params });
            const res = response.data || response;
            return {
                success: true,
                data: res.data,
                meta: res.meta || {},
                message: res.message || 'Risk listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related risks for asset');
        }
    }

    /**
     * Link Risks to Asset
     * @param {string} id - Asset ID
     * @param {Array<string>} riskIds - Array of Risk IDs to link
     */
    async addRisks(id, riskIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/risks`, { riskIds });
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                draftSaved: res.draftSaved === true,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link risks to asset');
        }
    }

    /**
     * Unlink Risk from Asset
     * @param {string} id - Asset ID
     * @param {string} riskId - Risk ID to unlink
     */
    async removeRisk(id, riskId) {
        try {
            const response = await this.api.client.delete(`${this.api.basePath}/${id}/risks/${riskId}`);
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                draftSaved: res.draftSaved === true,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink risk from asset');
        }
    }

    /**
     * Unlink multiple risks in one request (bulk UI; single pending change when approval is on)
     * @param {string} id - Asset ID
     * @param {Array<string>} riskIds - Risk IDs to unlink
     */
    async removeRisksBulk(id, riskIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/risks/unlink`, { pullRiskIds: riskIds });
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                draftSaved: res.draftSaved === true,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk unlink risks from asset');
        }
    }

    async getRelatedThreats(id, params = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/${id}/threats`, { params });
            const res = response.data || response;
            return {
                success: true,
                data: res.data,
                meta: res.meta || {},
                message: res.message || 'Threat listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related threats for asset');
        }
    }

    async addThreats(id, threatIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/threats`, { threatIds });
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                draftSaved: res.draftSaved === true,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link threats to asset');
        }
    }

    async removeThreat(id, threatId) {
        try {
            const response = await this.api.client.delete(`${this.api.basePath}/${id}/threats/${threatId}`);
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                draftSaved: res.draftSaved === true,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink threat from asset');
        }
    }

    async removeThreatsBulk(id, threatIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/threats/unlink`, { threatIds });
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk unlink threats from asset');
        }
    }

    async getrelatedControls(id, params = {}) {
        try {
            const response = await this.api.client.get(`${this.api.basePath}/${id}/scf-controls`, { params });
            const res = response.data || response;
            return {
                success: true,
                data: res.data,
                meta: res.meta || {},
                message: res.message || 'Kontrol listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch related controls for asset');
        }
    }

    async addScfControls(id, scfControlIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/scf-controls`, { scfControlIds });
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to link controls to asset');
        }
    }

    async removeScfControl(id, scfControlId) {
        try {
            const response = await this.api.client.delete(`${this.api.basePath}/${id}/scf-controls/${scfControlId}`);
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink control from asset');
        }
    }

    async removeScfControlsBulk(id, scfControlIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${id}/scf-controls/unlink`, { scfControlIds });
            const res = response.data || response;
            return {
                success: res.success !== false,
                data: res.data,
                message: res.message,
                pendingChangeId: res.pendingChangeId,
                approvalStatus: res.approvalStatus,
                merged: res.merged === true
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk unlink controls from asset');
        }
    }
}
